import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { Employer } from '@/types/employer'

// These employers are duplicates for InterviewwWatch and should not be shown in the dropdown for now
// Google, Tiktok, Deepmind, X
const employerIdsToExclude = [9, 33, 35, 157]

export const useEmployerStore = defineStore('employer', () => {
  const employers = ref<Employer[]>([])
  const roleNames = ref<string[]>([])
  const interviewTypes = ref<string[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const hasFetchedInitialData = ref(false)

  // New refs for employer options
  const employerOptions = ref<Employer[]>([])
  const nextPage = ref<string | null>(null)
  const isSearching = ref(false)
  const searchQuery = ref('')

  const fetchInitialData = async () => {
    if (hasFetchedInitialData.value || isLoading.value) {
      return
    }
    
    isLoading.value = true
    error.value = null
    
    try {
      const [employerResponse] = await Promise.all([
        api.get(API_ROUTES.GET_EMPLOYERS_OPTIONS)
      ])
      
      // Handle both possible response structures
      let employerData: Employer[] = []
      if (Array.isArray(employerResponse.data)) {
        employerData = employerResponse.data
      } else if (employerResponse.data.results) {
        employerData = employerResponse.data.results
      }
      
      employers.value = employerData.filter((employer: Employer) => !employerIdsToExclude.includes(employer.id))
      hasFetchedInitialData.value = true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch initial data'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchEmployerOptions = async (search: string = '', page: string | null = null, hasOpenRoles = false) => {
    isLoading.value = true
    error.value = null
    
    try {
      let url = page || `${API_ROUTES.GET_EMPLOYERS_OPTIONS}?search=${search}${hasOpenRoles ? '&has_open_roles=true' : ''}`
      
      // Ensure HTTPS in production to avoid mixed content errors
      if (page && window.location.protocol === 'https:' && url.startsWith('http:')) {
        url = url.replace('http:', 'https:')
      }
      
      const response = await api.get(url)
      
      if (page) {
        // Append results for pagination
        employerOptions.value = [...employerOptions.value, ...response.data.results]
      } else {
        // Replace results for new search
        employerOptions.value = response.data.results
      }
      
      nextPage.value = response.data.next
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch employer options'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const searchEmployers = async (query: string, hasOpenRoles = false) => {
    searchQuery.value = query
    isSearching.value = true
    await fetchEmployerOptions(query, null, hasOpenRoles)
    isSearching.value = false
  }

  const loadMoreEmployers = async () => {
    if (nextPage.value && !isLoading.value) {
      await fetchEmployerOptions(searchQuery.value, nextPage.value)
    }
  }

  /**
   * Ensure an employer is present in the options list.
   * Used after URL autofill to pin the selected employer so it is always
   * visible in the picker regardless of which paginated page was loaded.
   */
  const ensureEmployerInOptions = (employer: Pick<Employer, 'id' | 'name'> & { logo?: string | null; url?: string | null }) => {
    if (employerOptions.value.some((e) => e.id === employer.id)) return
    employerOptions.value = [
      {
        id: employer.id,
        name: employer.name,
        logo: employer.logo ?? null,
        url: employer.url ?? null,
        is_active: true,
        default_referrer: ''
      },
      ...employerOptions.value
    ]
  }

  const checkAndCreateEmployer = async (name: string) => {
    try {
      const checkResponse = await api.post(API_ROUTES.CHECK_EMPLOYER_EXISTENCE, { name })
      if (checkResponse.status === 200) {
        return checkResponse.data
      }
    } catch {
      // If check fails, create new employer
      const createResponse = await api.post(API_ROUTES.CREATE_EMPLOYER, { name })
      employers.value.push(createResponse.data)
      return createResponse.data
    }
  }

  /**
   * Update the internal notes for an employer
   * @param employerId - Employer ID
   * @param notes - New notes string
   */
  const updateEmployerNotes = async (employerId: number, notes: string) => {
    const response = await api.put(API_ROUTES.UPDATE_EMPLOYER(employerId), { outtalent_internal_notes: notes })
    // Update local employer if present
    const idx = employers.value.findIndex(e => e.id === employerId)
    if (idx !== -1) {
      employers.value[idx] = { ...employers.value[idx], ...response.data }
    }
    return response.data
  }

  return {
    employers,
    roleNames,
    interviewTypes,
    isLoading,
    error,
    hasFetchedInitialData,
    employerOptions,
    nextPage,
    isSearching,
    searchQuery,
    fetchInitialData,
    fetchEmployerOptions,
    searchEmployers,
    loadMoreEmployers,
    ensureEmployerInOptions,
    checkAndCreateEmployer,
    updateEmployerNotes
  }
}) 